import { LitElement, html } from 'lit';
import typographyStyles from '@cof/fs-typography-core/lib/typography.lit';
import { fs16CheckmarkCircleFilled } from '@cof/fs-icons';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { displayProgressBarEvent } from '@cof/refi-web-sdk/dist/utils/update-progress-bar.js';
import { DebugService } from '../../shared/services/debug.service.js';
import { style } from './fast-lane.component.style.js';
import '@cof/fs-modal-dialog-wc/fs-modal-dialog.js';
import { carLogo } from './assets/fast-lane.js';
import { stratum } from '../../../constants/stratum.js';
import { ApplicationService } from '../../services/application.service.js';
import { hideLoaderSpinnerEvent } from '../utils/host-app-events.js';

class RefiFastLane extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('FastLaneComponent');
    this.headingText2 = 'fast lane!';
    FsIcon.add(fs16CheckmarkCircleFilled);
  }

  static styles = [style, typographyStyles];

  static properties = {
    v2Routing: { type: Boolean, attribute: 'v2-routing', reflect: true },
    applicantFirstName: {
      type: String,
      attribute: 'applicant-first-name',
      reflect: true,
    },
  };

  get application() {
    return ApplicationService.Application;
  }

  updated(changes) {
    super.updated();
    if (changes.has('v2Routing') && changes.v2Routing !== this.v2Routing) {
      this.debug.log('Updating v2 host app routing to', this.v2Routing);
      ApplicationService.isV2Routing = this.v2Routing;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    ApplicationService.isV2Routing = this.v2Routing;
    document.dispatchEvent(
      new CustomEvent('hide-footer', { detail: { showFooter: false } })
    );
    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_HOSTAPP_FAST_LANE);
    displayProgressBarEvent(false);
  }

  firstUpdated() {
    hideLoaderSpinnerEvent(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.dispatchEvent(
      new CustomEvent('hide-footer', { detail: { showFooter: true } })
    );
  }

  getNameText() {
    const firstName =
      this.application.primaryApplicant?.firstName || this.applicantFirstName;
    if (firstName) {
      return `${firstName},`;
    }
    return '';
  }

  getHeadingText1() {
    if (this.getNameText()?.length > 0) {
      return `you’re in the `;
    }
    return 'You’re in the ';
  }

  render() {
    this.debug.log('Rendering Fast Lane Component');
    return html`
      <refi-track-pageview pagename="fast-lane-welcome"></refi-track-pageview>
      <div class="full-page">
        <div class="text-content">
          <div class="fast-lane-title">
            <fs-heading
              class="fs-text-weight--semibold title-text"
              level="1"
              size="large"
              variant="inverse"
              >${this.getNameText()}</fs-heading
            >
            <fs-heading
              class="fs-text-weight--semibold title-text"
              level="1"
              size="large"
              variant="inverse"
              >${this.getHeadingText1()}<span class="blue-text"
                >${this.headingText2}</span
              ></fs-heading
            >
          </div>
          <div class="body-content">
            <div class="body-text">
              <p
                class="fs-text fs-text-weight--semibold fs-text-color--inverse check-list-title"
                size="normal"
                variant="inverse"
              >
                Since you’re a Capital One customer, we can:
              </p>
              <div class="check-list-box">
                <div class="check-list-items">
                  <fs-icon
                    description="Checkmark icon"
                    class="blue-fill"
                    icon="fs-16-checkmark-circle-filled"
                    compact=""
                    aria-hidden="true"
                  ></fs-icon>
                  <fs-body-text size="normal" variant="inverse"
                    >Import your personal, car and loan info</fs-body-text
                  >
                </div>
                <div class="check-list-items">
                  <fs-icon
                    description="Checkmark icon"
                    class="blue-fill two-line-text-icon"
                    icon="fs-16-checkmark-circle-filled"
                    variant="inverse"
                    compact=""
                    aria-hidden="true"
                  ></fs-icon>
                  <fs-body-text size="normal" variant="inverse"
                    >Check for savings
                    <span style="text-decoration: underline"
                      >with no impact to your credit score</span
                    ></fs-body-text
                  >
                </div>
              </div>
            </div>
            <div class="cta-button-container">
              <fs-modal-dialog
                id="noImpactCreditPullModal"
                accessible-title="First, we’ll check your eligibility"
                mobile-layout="auto"
                aria-labelledby="noImpactCreditPullModal"
                size="small"
                dismissible
                @modal-dialog-open="${this.publishViewModal}"
                @modal-dialog-close="${this.publishCloseModal}"
              >
                <fs-heading
                  slot="modal-dialog-header"
                  level="2"
                  size="medium"
                  variant="primary"
                  >First, we’ll check your eligibility</fs-heading
                >
                <section
                  class="fs-body-text fs-body-text--normal credit-pull-modal-body-text"
                  slot="modal-dialog-body"
                >
                  <fs-body-text size="normal" variant="primary"
                    >I give Capital One permission to get my credit reports,
                    confirm my income, and get info about my car from state
                    vehicle records.
                    <span class="bold-text"
                      >This won’t impact your credit score.</span
                    >
                  </fs-body-text>
                  <fs-body-text size="normal" variant="primary"
                    >I’ve read and agree to Capital One’s
                    <a
                      href="https://www.capitalone.com/privacy/"
                      class="fs-link fs-link--primary"
                      target="_blank"
                      rel="noopener noreferrer"
                      @click="${this.publishPrivacy}"
                      >Privacy Policies</a
                    >
                    and
                    <a
                      href="https://www.capitalone.com/bank/disclosures/eddn/"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="fs-link fs-link--primary"
                      @click="${this.publishPaperless}"
                      >Paperless Agreement</a
                    >. I understand I must agree to both to apply for
                    refinancing and that I will receive all communications about
                    my refinance application and auto loan account
                    electronically, not on paper.
                  </fs-body-text>
                </section>
                <fs-modal-dialog-footer
                  footer-layout-swap-breakpoint="medium"
                  slot="modal-dialog-footer"
                >
                  <fs-button
                    variant="primary"
                    touched=""
                    class="fs-button fs-button--primary modal-btn"
                    @click="${this.modalAgree}"
                    >I agree</fs-button
                  >
                </fs-modal-dialog-footer>

                <fs-button
                  variant="primary"
                  type="button"
                  class="get-started-button"
                  @click="${this.publishGetStarted}"
                  >Apply individually</fs-button
                >
              </fs-modal-dialog>
              <fs-button
                variant="primary"
                inverted=""
                outlined=""
                touched=""
                class="coapp-button fs-button fs-button--primary modal-btn"
                @click="${this.clickAddCoapplicantLink}"
                >Or apply with a co-borrower</fs-button
              >
            </div>
          </div>
        </div>
        <div class="car-footer">
          <div class="bottom-graphic">
            <div class="car-logo">${carLogo}</div>
            <div class="semicircle"></div>
          </div>
        </div>
      </div>
    `;
  }

  publishPrivacy() {
    stratum.publishTag(
      TagKey.FAST_LANE_INVISIBLE_PREQUAL_CONSENT_PRIVACY_POLICY
    );
  }

  publishPaperless() {
    stratum.publishTag(
      TagKey.FAST_LANE_INVISIBLE_PREQUAL_CONSENT_PAPERLESS_AGREEMENT
    );
  }

  publishGetStarted() {
    stratum.publishTag(TagKey.FAST_LANE_APP_START);
  }

  publishViewModal() {
    stratum.publishTag(TagKey.VIEW_FAST_LANE);
  }

  publishCloseModal() {
    stratum.publishTag(TagKey.FAST_LANE_INVISIBLE_PREQUAL_CONSENT_MODAL_CLOSE);
  }

  clickAddCoapplicantLink() {
    stratum.publishTag(TagKey.FAST_LANE_CO_APPLICANT_OR_REVIEW_INFO);

    ApplicationService.Application = {
      ...ApplicationService.Application,
      applicationType: 'JOINT',
    };

    document.dispatchEvent(
      new CustomEvent('start-new-app', {
        detail: { fromFastLane: true, coapplicant: true },
      })
    );
  }

  modalAgree() {
    ApplicationService.Application = {
      ...ApplicationService.Application,
      applicationType: 'INDIVIDUAL',
      hasConsent: true,
    };
    stratum.publishTag(TagKey.FAST_LANE_INVISIBLE_PREQUAL_CONSENT_SUBMIT);
    document.dispatchEvent(
      new CustomEvent('start-new-app', { detail: { fromFastLane: true } })
    );
  }
}

customElements.define('refi-fast-lane', RefiFastLane);
